"use client";

import ProfileIcon from "@/assets/ProfileIcon";
import { memo, useCallback, useEffect, useState } from "react";

const tips = [
  {
    id: 1,
    title: "Keep your profile active",
    description: "Active job seekers get 10× more interest from recruiters",
  },
  {
    id: 2,
    title: "Complete your profile",
    description: "Recruiters seek 100% profiles - complete yours to stand out!",
  },
  {
    id: 3,
    title: "Open recruiter outreach email",
    description: "Open at least one recruiter outreach email to boost activity",
  },
  {
    id: 4,
    title: "Upload an updated resume",
    description: "Keep your resume current to attract more opportunities",
  },
  {
    id: 5,
    title: "Apply to relevant jobs",
    description: "Regular job applications increase your visibility",
  },
];

// Carousel auto-rotate interval in milliseconds
const CAROUSEL_INTERVAL = 4000; // 4 seconds

const ActivityLevelTips = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % tips.length);
    }, CAROUSEL_INTERVAL);

    return () => clearInterval(interval);
  }, [isHovered]);

  const handleDotClick = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const getDotSize = (index: number): number => {
    const distance = Math.abs(index - activeIndex);
    if (distance === 0) return 6; // Active dot: 6px
    if (distance === 1) return 6; // Adjacent dots: 6px
    if (distance === 2) return 4; // 2 away: 4px
    return 2; // Far dots: 2px
  };

  const getDotOpacity = (index: number): number => {
    return index === activeIndex ? 1 : 0.3;
  };

  const currentTip = tips[activeIndex];

  return (
    <div
      className="flex w-full flex-col gap-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="text-sm font-bold leading-5 text-[#17142A]">
        Tips to improve your activity level
      </div>

      <div className="flex min-h-[54px] items-start gap-3">
        <div className="flex-shrink-0">
          <ProfileIcon width={16} height={16} />
        </div>

        <div
          className="animate-tip-fade-in flex flex-col gap-1.5"
          key={currentTip.id}
        >
          <div className="text-xs font-medium leading-4 text-[#17142A]">
            {currentTip.title}
          </div>
          <div className="text-xs font-normal leading-4 text-[#777584]">
            {currentTip.description}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-[9px]">
        {tips.map((_, index) => {
          const size = getDotSize(index);
          const opacity = getDotOpacity(index);

          return (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className="cursor-pointer rounded-full bg-[#59566C] transition-all duration-300"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                opacity,
              }}
              aria-label={`Go to tip ${index + 1}`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default memo(ActivityLevelTips);
